import SportIcon from '@/components/SportIcon';
/**
 * PlayerCategoriesPage.jsx
 * Manage player categories: Champion, Advanced, Non-Playing + custom
 * - Champion / Advanced are skill levels — NOT linked to Platinum matches
 * - Platinum is marked per-player on the Team Detail page before tournament
 * - Non-Playing = org member who does not participate in matches
 */
import { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, Star, Shield, ChevronDown, ChevronUp, Users } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import useAuthStore from '@/stores/authStore';
import { toast } from 'sonner';

const PRESET_COLORS = ['#f5c518','#3b82f6','#22c55e','#ef4444','#8b5cf6','#f97316','#ec4899','#14b8a6'];

const SPORT_ICONS = {
  table_tennis: '🏓', badminton: '🏸', chess: '♟️',
  carrom: '🎯', pickleball: '🥒', squash: '🎾',
  cricket: '🏏', football: '⚽', default: '🏆',
};

export default function PlayerCategoriesPage() {
  const { activeOrgSlug, user } = useAuthStore();
  const orgSlug = user?.memberships?.[0]?.slug || activeOrgSlug || 'btcc';
  const token = () => localStorage.getItem('btccp_access_token');

  const [categories, setCategories]     = useState([]);
  const [sports, setSports]             = useState([]);
  const [loading, setLoading]           = useState(true);
  const [addOpen, setAddOpen]           = useState(false);
  const [editTarget, setEditTarget]     = useState(null);
  const [form, setForm]                 = useState({ name: '', color: '#8b5cf6', is_non_playing: false });
  const [saving, setSaving]             = useState(false);

  // Drill-down state
  const [drillCategory, setDrillCategory]     = useState(null);
  const [drillPlayers, setDrillPlayers]       = useState([]);
  const [drillLoading, setDrillLoading]       = useState(false);
  const [teamAssignments, setTeamAssignments] = useState({});

  useEffect(() => {
    fetchCategories();
    fetchSports();
    fetchTeamAssignments();
  }, [orgSlug]);

  const fetchCategories = async () => {
    setLoading(true);
    const res = await fetch(`/api/orgs/${orgSlug}/player-categories`,
      { headers: { 'Authorization': `Bearer ${token()}` } });
    const data = await res.json();
    setCategories(Array.isArray(data) ? data : []);
    setLoading(false);
  };

  const fetchSports = async () => {
    try {
      const res = await fetch(`/api/orgs/${orgSlug}/sports`, { headers: { 'Authorization': `Bearer ${token()}` } });
      setSports(Array.isArray(await res.json()) ? await res.json() : []);
    } catch (_) {}
  };

  const fetchTeamAssignments = async () => {
    try {
      const res = await fetch(`/api/orgs/${orgSlug}/player-team-assignments`,
        { headers: { 'Authorization': `Bearer ${token()}` } });
      if (res.ok) setTeamAssignments(await res.json());
    } catch (_) {}
  };

  const handleCategoryClick = async (cat) => {
    if (drillCategory?.code === cat.code) { setDrillCategory(null); setDrillPlayers([]); return; }
    setDrillCategory(cat);
    setDrillLoading(true);
    try {
      const res = await fetch(`/api/orgs/${orgSlug}/players/by-category/${cat.code}`,
        { headers: { 'Authorization': `Bearer ${token()}` } });
      setDrillPlayers(Array.isArray(await res.json()) ? await res.json() : []);
    } catch (_) { toast.error('Failed to load players'); }
    finally { setDrillLoading(false); }
  };

  const buildSportGroups = (players) => {
    const groups = {};
    players.forEach(p => {
      const ss = p.sports || [];
      if (ss.length === 0) { groups['__none__'] = [...(groups['__none__']||[]), p]; }
      else ss.forEach(sc => { groups[sc] = [...(groups[sc]||[]), p]; });
    });
    return groups;
  };

  const getSportName = (code) => {
    if (code === '__none__') return 'No Sport Assigned';
    return sports.find(s => s.code === code)?.name || code.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase());
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Category name is required'); return; }
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), color: form.color, is_non_playing: form.is_non_playing };
      if (editTarget) {
        const res = await fetch(`/api/orgs/${orgSlug}/player-categories/${editTarget.id}`, {
          method: 'PATCH',
          headers: { 'Authorization': `Bearer ${token()}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) { const d=await res.json(); toast.error(d.detail||'Failed'); return; }
        toast.success(`${form.name} updated`);
      } else {
        const res = await fetch(`/api/orgs/${orgSlug}/player-categories`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token()}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) { const d=await res.json(); toast.error(d.detail||'Failed'); return; }
        toast.success(`${form.name} category added`);
      }
      setAddOpen(false); setEditTarget(null);
      fetchCategories();
    } finally { setSaving(false); }
  };

  const handleDelete = async (cat) => {
    if (!confirm(`Delete "${cat.name}" category?`)) return;
    const res = await fetch(`/api/orgs/${orgSlug}/player-categories/${cat.id}`, {
      method: 'DELETE', headers: { 'Authorization': `Bearer ${token()}` },
    });
    if (res.ok) { toast.success(`${cat.name} deleted`); fetchCategories(); }
    else { const d=await res.json(); toast.error(d.detail||'Cannot delete'); }
  };

  const openAdd = () => {
    setEditTarget(null);
    setForm({ name: '', color: '#8b5cf6', is_non_playing: false });
    setAddOpen(true);
  };

  const openEdit = (e, cat) => {
    e.stopPropagation();
    setEditTarget(cat);
    setForm({ name: cat.name, color: cat.color, is_non_playing: !!cat.is_non_playing });
    setAddOpen(true);
  };

  const isBuiltIn = (cat) => ['champion','advanced','non_playing'].includes(cat.code);

  const sportGroups = drillCategory ? buildSportGroups(drillPlayers) : {};

  const getCategoryMeta = (cat) => {
    if (cat.is_non_playing) return { icon: '⛔', badge: 'Non-Playing', badgeColor: '#8a93a3', desc: 'Org member only — does not participate in any match' };
    if (cat.code === 'champion') return { icon: '👑', badge: 'Playing', badgeColor: '#f5c518', desc: 'Playing member · Platinum slots assigned per team before tournament' };
    return { icon: '⭐', badge: 'Playing', badgeColor: '#3b82f6', desc: 'Playing member · Plays regular match slots (R1, R2, R3...)' };
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="player-categories-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#f0f2f7] mb-1">Player Categories</h1>
          <p className="text-[#8a93a3]">Define member types — click a category to see its players grouped by sport</p>
        </div>
        <Button onClick={openAdd} className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]">
          <Plus className="h-4 w-4 mr-2"/>Add Category
        </Button>
      </div>

      {/* Info card — updated to reflect platinum-per-roster model */}
      <Card className="bg-[#161a26] border-[rgba(59,130,246,0.3)] mb-6">
        <CardContent className="p-4 flex items-start gap-3">
          <Star className="h-5 w-5 text-[#3b82f6] flex-shrink-0 mt-0.5"/>
          <div className="text-sm text-[#8a93a3]">
            <p className="text-[#f0f2f7] font-medium mb-1">Categories are skill levels, not match tiers</p>
            <p>Champion / Advanced are descriptive labels. <strong className="text-[#f0f2f7]">Platinum match slots</strong> are assigned per-player on the <strong className="text-[#f0f2f7]">Team Detail page</strong> before each tournament.</p>
            <p className="mt-1"><strong className="text-[#8a93a3]">Non-Playing</strong> members are part of the organisation but do not play in matches.</p>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i=><Skeleton key={i} className="h-20 bg-[#1d2230]"/>)}</div>
      ) : (
        <div className="space-y-3">
          {categories.map(cat => {
            const isExpanded = drillCategory?.code === cat.code;
            const meta = getCategoryMeta(cat);
            return (
              <div key={cat.id}>
                <Card
                  onClick={() => handleCategoryClick(cat)}
                  className={`border-[#252a37] cursor-pointer transition-all duration-200 ${isExpanded ? 'bg-[#1a1f2e] ring-1 ring-[#f5c518]/40' : 'bg-[#161a26] hover:bg-[#1a1f2e]'}`}
                  style={{ borderLeftWidth: 4, borderLeftColor: cat.color }}
                  data-testid={`category-card-${cat.code}`}
                >
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="h-10 w-10 rounded-xl flex items-center justify-center font-bold text-white text-base flex-shrink-0"
                      style={{ backgroundColor: cat.color }}>
                      {meta.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-[#f0f2f7]">{cat.name}</h3>
                        <Badge className="text-xs border"
                          style={{ backgroundColor: `${meta.badgeColor}20`, color: meta.badgeColor, borderColor: `${meta.badgeColor}50` }}>
                          {meta.badge}
                        </Badge>
                        {isBuiltIn(cat) && (
                          <Badge className="bg-[#1d2230] text-[#8a93a3] border border-[#252a37] text-xs flex items-center gap-1">
                            <Shield className="h-2.5 w-2.5"/>Built-in
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-[#8a93a3] mt-0.5">
                        {meta.desc}
                        {isExpanded && drillPlayers.length > 0 && <span className="ml-2 text-[#f5c518]">· {drillPlayers.length} players</span>}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button size="sm" variant="ghost" onClick={(e) => openEdit(e, cat)}
                        className="text-[#8a93a3] hover:text-[#f0f2f7] h-8 w-8 p-0">
                        <Edit2 className="h-3.5 w-3.5"/>
                      </Button>
                      {!isBuiltIn(cat) && (
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDelete(cat); }}
                          className="text-[#8a93a3] hover:text-[#ef4444] h-8 w-8 p-0">
                          <Trash2 className="h-3.5 w-3.5"/>
                        </Button>
                      )}
                      <div className="text-[#8a93a3]">
                        {isExpanded ? <ChevronUp className="h-4 w-4"/> : <ChevronDown className="h-4 w-4"/>}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Drill-down panel */}
                {isExpanded && (
                  <div className="mt-1 mb-2 ml-4 pl-4 border-l-2" style={{ borderLeftColor: cat.color + '60' }}>
                    {drillLoading ? (
                      <div className="space-y-2 py-3">{[1,2,3].map(i=><Skeleton key={i} className="h-12 bg-[#1d2230]"/>)}</div>
                    ) : drillPlayers.length === 0 ? (
                      <div className="py-6 text-center">
                        <Users className="h-8 w-8 text-[#8a93a3] mx-auto mb-2"/>
                        <p className="text-[#8a93a3] text-sm">No players in {cat.name} category yet</p>
                      </div>
                    ) : (
                      <div className="space-y-3 py-3">
                        {Object.entries(buildSportGroups(drillPlayers)).map(([sportCode, sportPlayers]) => (
                          <Card key={sportCode} className="bg-[#0f1219] border-[#252a37] overflow-hidden">
                            <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#1e2435]">
                              <div className="flex items-center gap-2">
                                <SportIcon sport={sportCode} size={18}/>
                                <span className="font-semibold text-[#f5c518] text-sm">{getSportName(sportCode)}</span>
                              </div>
                              <span className="text-xs font-semibold text-[#8a93a3] bg-[#1d2230] px-2 py-0.5 rounded-full">{sportPlayers.length}</span>
                            </div>
                            {sportPlayers.map((player, i) => {
                              const name = player.full_name || `${player.first_name} ${player.last_name}`.trim();
                              const teamInfo = teamAssignments[player.id];
                              return (
                                <div key={player.id || i} className="flex items-center gap-3 px-4 py-2.5 border-b border-[#1e2435]/60 last:border-0 hover:bg-[#161a26] transition-colors">
                                  <div className="h-8 w-8 rounded-full flex-shrink-0 flex items-center justify-center font-bold text-sm"
                                    style={{ backgroundColor: `${cat.color}25`, color: cat.color }}>
                                    {player.photo_url ? <img src={player.photo_url} className="h-full w-full rounded-full object-cover" alt={name}/> : name.split(' ').map(n=>n[0]).slice(0,2).join('')}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-[#f0f2f7] text-sm truncate">{name}</p>
                                  </div>
                                  {teamInfo ? (
                                    <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold flex-shrink-0"
                                      style={{ backgroundColor: `${teamInfo.team_color}20`, color: teamInfo.team_color, border: `1px solid ${teamInfo.team_color}40` }}>
                                      {teamInfo.team_name}
                                    </span>
                                  ) : (
                                    <span className="text-[10px] px-2 py-0.5 rounded-full font-medium flex-shrink-0 bg-[#1d2230] text-[#8a93a3] border border-[#252a37]">
                                      Unassigned
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={addOpen} onOpenChange={o => { setAddOpen(o); if (!o) setEditTarget(null); }}>
        <DialogContent className="bg-[#161a26] border-[#252a37] max-w-sm" data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7]">
              {editTarget ? `Edit: ${editTarget.name}` : 'Add New Category'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[#8a93a3]">Category Name *</Label>
              <Input value={form.name} onChange={e => setForm({...form, name:e.target.value})}
                placeholder="e.g., Elite, Veteran, Junior"
                className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1"
                data-testid="category-name-input"/>
            </div>
            <div>
              <Label className="text-[#8a93a3] mb-2 block">Color</Label>
              <div className="flex items-center gap-2 flex-wrap">
                {PRESET_COLORS.map(c => (
                  <button key={c} onClick={() => setForm({...form, color:c})}
                    className={`h-7 w-7 rounded-lg transition-transform hover:scale-110 ${form.color===c?'ring-2 ring-white ring-offset-1 ring-offset-[#161a26]':''}`}
                    style={{ backgroundColor: c }}/>
                ))}
                <input type="color" value={form.color} onChange={e => setForm({...form, color:e.target.value})}
                  className="h-7 w-7 rounded-lg cursor-pointer border-0 p-0.5 bg-transparent"/>
              </div>
            </div>
            <div>
              <Label className="text-[#8a93a3] mb-2 block">Match Status</Label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: false, label: 'Playing',     desc: 'Participates in matches',     icon: '⭐', color: '#3b82f6' },
                  { value: true,  label: 'Non-Playing', desc: 'Org member, no matches',      icon: '⛔', color: '#8a93a3' },
                ].map(opt => (
                  <div key={String(opt.value)}
                    onClick={() => setForm({...form, is_non_playing: opt.value})}
                    className={`p-3 rounded-lg cursor-pointer border-2 transition-colors ${
                      form.is_non_playing === opt.value
                        ? 'border-[#f5c518] bg-[rgba(245,197,24,0.08)]'
                        : 'border-[#252a37] hover:border-[#8a93a3]'
                    }`}>
                    <p className="text-sm font-semibold flex items-center gap-1.5" style={{ color: form.is_non_playing===opt.value ? opt.color : '#8a93a3' }}>
                      <span>{opt.icon}</span>{opt.label}
                    </p>
                    <p className="text-xs text-[#8a93a3] mt-0.5">{opt.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} className="border-[#252a37] text-[#f0f2f7]">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]"
              data-testid="category-save-button">
              {saving ? 'Saving...' : editTarget ? 'Save Changes' : 'Add Category'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
